import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import publicApi from '@/api/public';
import { apiUrl } from '@/api/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { setAnonymousUserPreferences, getAnonymousUserPreferences, clearAnonymousUserPreferences } from '@/utils/anonymousAuth';
import { getBrowserFingerprint } from '@/utils/auth';
import { cn } from '@/lib/utils';
import { useDiscovery } from '@/context/DiscoveryContext';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { formatBoardLabel } from '@/utils/boardUtils';

export default function AnonymousOnboardingModal({ onComplete }) {
  const navigate = useNavigate();
  const { boards, grades, loadingBoardsGrades } = useDiscovery();
  const [boardId, setBoardId] = useState('');
  const [gradeId, setGradeId] = useState('');
  // Always use 'student' as user_type
  const userType = 'student';
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const [isOtherBoard, setIsOtherBoard] = useState(false);
  const [customCurriculum, setCustomCurriculum] = useState('');

  // Constants for others
  const OTHERS_GRADES = Array.from({ length: 12 }, (_, i) => (i + 1).toString());

  // Check if user already has preferences on mount
  useEffect(() => {
    const preferences = getAnonymousUserPreferences();
    if (preferences && preferences.boardId && preferences.gradeId && showModal) {
      // User already has preferences, navigate to student learn page
      navigate('/student/learn', { replace: true });
      if (onComplete) onComplete();
      setShowModal(false);
    }
  }, [navigate, onComplete, showModal]);


  const handleContinue = async () => {
    if (isOtherBoard) {
      if (!customCurriculum.trim() || !gradeId) {
        toast.error('Please enter curriculum name and select grade');
        return;
      }

      setLoading(true);
      const cleanCurriculum = customCurriculum.trim();
      const reqCurriculumStr = `${cleanCurriculum} (Grade ${gradeId})`.trim();

      // Submit curriculum request in background (idempotent, never blocks user)
      try {
        const fingerprintId = await getBrowserFingerprint();
        await publicApi.post('superadmin/curriculum-requests/', {
          curriculum_name: cleanCurriculum,
          grade: gradeId,
          user_type: userType,
          user_name: 'Anonymous User',
          fingerprint_id: fingerprintId
        });
        try {
          trackEvent('curriculum_request_submitted', attachUserProps({
            page: 'anonymous_onboarding',
            curriculum_name: cleanCurriculum,
            grade_id: gradeId,
            board_name: 'Others',
            request_type: 'guest_curriculum_gap',
          }));
        } catch { }
      } catch (error) {
        console.warn('Curriculum request background submission notice:', error?.response?.data?.error || error.message);
      }

      // Set anonymous sample preview preferences (CBSE Grade 10, Chapter 118)
      setAnonymousUserPreferences({
        userType,
        boardId: 1,
        gradeId: 5,
        boardName: 'Central Board',
        gradeName: 'Grade 10',
        isCustomCurriculum: true,
        requestedCurriculum: reqCurriculumStr,
      });
      window.dispatchEvent(new CustomEvent('anonymousPreferencesUpdated'));
      localStorage.setItem('is_guest_session', 'true');
      localStorage.setItem('student_board_id', '1');
      localStorage.setItem('student_grade_id', '5');

      toast.success(`Welcome! Exploring Budlee AI with sample preview for ${cleanCurriculum} (Grade ${gradeId}).`);

      setTimeout(() => {
        navigate('/student/learn', { replace: true });
        if (onComplete) onComplete();
        setLoading(false);
        setShowModal(false);

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('showUserGuide'));
        }, 500);
      }, 300);
      return;
    }

    if (!boardId || !gradeId) {
      toast.error('Please select curriculum and grade');
      return;
    }

    setLoading(true);

    const isCustomGrade = String(gradeId).startsWith('custom_');
    if (isCustomGrade) {
      const targetGradeNum = String(gradeId).replace('custom_', '');
      const selectedBoard = boards.find(b => b.id === parseInt(boardId));
      const boardLabel = selectedBoard?.board_name || selectedBoard?.board_code || 'CBSE';
      const reqCurriculumStr = `${boardLabel} (Grade ${targetGradeNum})`.trim();

      // Submit request in background
      try {
        const fingerprintId = await getBrowserFingerprint();
        await publicApi.post('superadmin/curriculum-requests/', {
          curriculum_name: boardLabel,
          grade: targetGradeNum,
          user_type: userType,
          user_name: 'Anonymous User',
          fingerprint_id: fingerprintId
        });
      } catch (err) {
        console.warn('Failed to submit curriculum request for custom grade:', err);
      }

      // Set preview preferences
      setAnonymousUserPreferences({
        userType,
        boardId: 1,
        gradeId: 5,
        boardName: 'Central Board',
        gradeName: 'Grade 10',
        isCustomCurriculum: true,
        requestedCurriculum: reqCurriculumStr,
      });
      window.dispatchEvent(new CustomEvent('anonymousPreferencesUpdated'));
      localStorage.setItem('is_guest_session', 'true');
      localStorage.setItem('student_board_id', '1');
      localStorage.setItem('student_grade_id', '5');

      toast.success(`Welcome! Exploring Budlee AI with sample preview for ${boardLabel} Grade ${targetGradeNum}.`);

      setTimeout(() => {
        navigate('/student/learn', { replace: true });
        if (onComplete) onComplete();
        setLoading(false);
        setShowModal(false);

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('showUserGuide'));
        }, 500);
      }, 300);
      return;
    }

    // Standard selected board and grade
    const selectedBoard = boards.find(b => b.id === parseInt(boardId));
    const selectedGrade = grades.find(g => g.id === parseInt(gradeId));

    // Store anonymous user preferences
    setAnonymousUserPreferences({
      userType,
      boardId: parseInt(boardId),
      gradeId: parseInt(gradeId),
      boardName: selectedBoard?.board_code || selectedBoard?.board_name || '',
      gradeName: selectedGrade?.grade_name || '',
      isCustomCurriculum: false,
    });
    window.dispatchEvent(new CustomEvent('anonymousPreferencesUpdated'));
    localStorage.setItem('is_guest_session', 'true');
    localStorage.setItem('student_board_id', String(boardId));
    localStorage.setItem('student_grade_id', String(gradeId));
    try {
      trackEvent('anonymous_onboarding_completed', attachUserProps({
        page: 'anonymous_onboarding',
        board_id: parseInt(boardId),
        board_name: selectedBoard?.board_code || selectedBoard?.board_name || '',
        grade_id: parseInt(gradeId),
        grade_name: selectedGrade?.grade_name || '',
      }));
    } catch { }

    // Navigate to student page
    const homePath = '/student/learn';

    toast.success('Welcome! Explore our platform as a guest.');

    setTimeout(() => {
      navigate(homePath, { replace: true });
      if (onComplete) onComplete();
      setLoading(false);
      setShowModal(false);

      // Dispatch showUserGuide event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('showUserGuide'));
      }, 500);
    }, 300);
  };

  const handleChangeSelection = () => {
    clearAnonymousUserPreferences();
    setShowModal(true);
    // Reset to initial state
    setBoardId('');
    setGradeId('');
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
      <Card className={cn(
        "fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))] bg-[hsl(var(--card-bg))] text-foreground shadow-2xl border border-[hsl(var(--card-border))] flex flex-col overflow-y-auto overflow-x-hidden transition-all duration-300 rounded-2xl",
        "md:max-h-[90vh] md:max-w-lg md:w-full"
      )}>
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center">
            <img
              src="/logo_tagline_black.png"
              alt="Budlee AI Logo"
              className="dark:hidden h-14 sm:h-16 w-auto max-w-[260px] sm:max-w-[300px] object-contain"
            />
            <img
              src="/logo_tagline_white.png"
              alt="Budlee AI Logo"
              className="hidden dark:block h-14 sm:h-16 w-auto max-w-[260px] sm:max-w-[300px] object-contain"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Type Selection - Student Only */}
          <div className="space-y-2">
            <label htmlFor="board" className="text-sm font-medium">
              Board
            </label>
            <Select
              value={boardId}
              onValueChange={(val) => {
                setBoardId(val);
                setGradeId(''); // Reset grade whenever board changes
                if (val === 'others') {
                  setIsOtherBoard(true);
                } else {
                  setIsOtherBoard(false);
                }
              }}
              disabled={loadingBoardsGrades}
            >
              <SelectTrigger id="board" className="w-full h-11 px-4 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-xl text-foreground text-sm font-normal focus:ring-2 focus:ring-[hsl(var(--green-bright))]">
                <SelectValue placeholder={loadingBoardsGrades ? "Loading..." : "Select curriculum"} />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border text-popover-foreground z-[15000] max-h-72 w-full min-w-[var(--radix-select-trigger-width)]">
                {boards.map((board) => (
                  <SelectItem key={board.id} value={board.id.toString()}>
                    {formatBoardLabel(board)}
                  </SelectItem>
                ))}
                <SelectItem key="others" value="others">+ Other / Not Listed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isOtherBoard && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <label htmlFor="custom-curriculum" className="text-sm font-medium text-foreground">
                Specify Curriculum / Board <span className="text-emerald-500">*</span>
              </label>
              <Input
                id="custom-curriculum"
                placeholder="e.g. ICSE, IGCSE, State Board, IB..."
                value={customCurriculum}
                onChange={(e) => setCustomCurriculum(e.target.value)}
                className="w-full h-11 px-4 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-xl text-foreground text-sm font-normal focus:ring-2 focus:ring-[hsl(var(--green-bright))]"
              />
            </div>
          )}

          {/* Grade Selection */}
          <div className="space-y-2">
            <label htmlFor="grade" className="text-sm font-medium">
              Grade
            </label>
            <Select value={gradeId} onValueChange={setGradeId} disabled={loadingBoardsGrades && !isOtherBoard}>
              <SelectTrigger id="grade" className="w-full h-11 px-4 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-xl text-foreground text-sm font-normal focus:ring-2 focus:ring-[hsl(var(--green-bright))]">
                <SelectValue placeholder={loadingBoardsGrades && !isOtherBoard ? "Loading..." : "Select grade"} />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border text-popover-foreground z-[15000] max-h-72 w-full min-w-[var(--radix-select-trigger-width)]">
                {isOtherBoard ? (
                  OTHERS_GRADES.map((grade) => (
                    <SelectItem key={grade} value={grade}>
                      Grade {grade}
                    </SelectItem>
                  ))
                ) : (
                  <>
                    {grades.map((grade) => (
                      <SelectItem key={grade.id} value={grade.id.toString()}>
                        {grade.grade_name}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom_11">Grade 11</SelectItem>
                    <SelectItem value="custom_12">Grade 12</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            disabled={
              (isOtherBoard ? (!customCurriculum.trim() || !gradeId) : (!boardId || !gradeId)) ||
              loading ||
              (loadingBoardsGrades && !isOtherBoard)
            }
            className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold shadow-md shadow-emerald-600/20 border-0"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Continue as Guest'
            )}
          </Button>

          {/* Sign Up Link */}
          <div className="text-center pt-4 border-t border-[hsl(var(--card-border))]">
            <p className="text-sm text-muted-foreground">
              Want full access?{' '}
              <button
                onClick={() => navigate('/signup')}
                className="text-[hsl(var(--green-bright))] dark:text-emerald-400 hover:underline font-semibold"
              >
                Create an account
              </button>
            </p>
          </div>

          {/* Change Selection (if preferences already exist) */}
          {getAnonymousUserPreferences() && (
            <div className="pt-2 border-t border-[hsl(var(--card-border))]">
              <Button
                onClick={handleChangeSelection}
                variant="outline"
                size="sm"
                className="w-full bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))]"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Change Selection
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
